'use client'

import { Box, Stack } from '@mantine/core'
import { DataTable } from 'mantine-datatable'
import { useMemo, useState } from 'react'

import { SectionErrorBoundary } from '@/components/error/boundary'
import { useEquityBaskets } from '@/services/equity/baskets'
import { gotoCdcWebSignupPage } from '@/utils'

import CursorPagination from '../CursorPagination'
import EmptyState from '../EmptyState'
import StockTableSkeleton from '../Skeleton'
import styles from '../styles.module.css'
import { useBasketColumns } from './columns'

// Static client-side paging: the full list is fetched once (no UI paging of
// the API, WWW-5085), then sliced locally — currently ~130 rows.
const BASKETS_PAGE_SIZE = 20

export default function BasketsTableWrapper() {
  return (
    <SectionErrorBoundary>
      <BasketsTable />
    </SectionErrorBoundary>
  )
}

function BasketsTable() {
  const { data, isPending, isFetching } = useEquityBaskets()
  const columns = useBasketColumns()
  const [currentPage, setCurrentPage] = useState(1)

  const allRecords = useMemo(() => data ?? [], [data])
  const pageCount = Math.max(1, Math.ceil(allRecords.length / BASKETS_PAGE_SIZE))
  // Clamp after a refetch that shrinks the list (e.g. baskets delisted).
  const safePage = Math.min(currentPage, pageCount)
  const records = useMemo(
    () => allRecords.slice((safePage - 1) * BASKETS_PAGE_SIZE, safePage * BASKETS_PAGE_SIZE),
    [allRecords, safePage],
  )

  // Manual row skeletons on first load (the DataTable loading overlay only
  // blurs existing rows, and there are none yet).
  if (isPending) return <StockTableSkeleton />

  if (!isFetching && allRecords.length === 0) return <EmptyState />

  return (
    // The extra `basketsRoot` class scopes the basket-specific rules (desktop
    // width cap + mobile collapse) so they don't leak onto the Trending table,
    // which reuses the same styles.root module.
    <Box pos="relative" className={styles.basketsRoot}>
      <Stack gap="16px">
        <DataTable
          fetching={isFetching}
          textSelectionDisabled
          records={records}
          columns={columns}
          idAccessor="id"
          // Pin the basket column (mirrors the crypto BasketsTable); on mobile
          // the CSS unpins it again since the other columns are hidden.
          pinFirstColumn
          // Whole row → cdc-web signup, same behavior as the Trade link and the
          // crypto basket cards.
          onRowClick={gotoCdcWebSignupPage}
          /* Styling */
          classNames={{
            root: styles.root,
            header: styles.header,
          }}
          minHeight="500px"
          backgroundColor="var(--bg-app)"
          withRowBorders={false}
          highlightOnHover
          highlightOnHoverColor="var(--bg-surface-secondary)"
        />
        <CursorPagination
          currentPage={safePage}
          loadedPageCount={pageCount}
          totalRecords={allRecords.length}
          isFetching={isFetching}
          recordsPerPage={BASKETS_PAGE_SIZE}
          onChange={page => setCurrentPage(page)}
        />
      </Stack>
    </Box>
  )
}
